/**
 * Where did this visitor come from?
 *
 * Detected on landing and kept in localStorage so it survives the whole visit.
 * A later arrival from a *different* non-direct source overwrites it - last
 * non-direct click wins, the same model Google Analytics uses.
 *
 * The result rides along with every order (Order.source) and feeds the
 * anonymous visit counter behind Admin -> Analytics -> Traffic sources.
 */

import api from "../api/client";

const LS_KEY = "777_attribution";
const SESSION_KEY = "777_visit_session";
const CONSENT_KEY = "777_cookie_consent";
const TTL_DAYS = 30;

const DIRECT = "Direct";

// Referring hostname -> label. First match wins, so keep the specific ones up top.
const REFERRER_RULES = [
  [/(^|\.)instagram\.com$|(^|\.)ig\.me$/i, "Instagram"],
  [/(^|\.)threads\.(net|com)$/i, "Threads"],
  [/(^|\.)facebook\.com$|(^|\.)fb\.(com|me)$/i, "Facebook"],
  [/(^|\.)tiktok\.com$/i, "TikTok"],
  [/(^|\.)youtube\.com$|(^|\.)youtu\.be$/i, "YouTube"],
  [/(^|\.)google\./i, "Google"],
  [/(^|\.)bing\.com$/i, "Bing"],
  [/(^|\.)duckduckgo\.com$/i, "DuckDuckGo"],
  [/(^|\.)yahoo\./i, "Yahoo"],
  [/(^|\.)yandex\./i, "Yandex"],
  [/(^|\.)pinterest\./i, "Pinterest"],
  [/(^|\.)(twitter\.com|x\.com|t\.co)$/i, "X (Twitter)"],
  [/(^|\.)linkedin\.com$|(^|\.)lnkd\.in$/i, "LinkedIn"],
  [/(^|\.)snapchat\.com$/i, "Snapchat"],
  [/(^|\.)reddit\.com$/i, "Reddit"],
  [/(^|\.)t\.me$|(^|\.)telegram\.(org|me)$/i, "Telegram"],
  [/(^|\.)whatsapp\.com$/i, "WhatsApp"],
  [/(^|\.)viber\.com$/i, "Viber"],
];

// What the agency may put in utm_source, mapped onto our labels
const UTM_ALIASES = {
  ig: "Instagram",
  instagram: "Instagram",
  instagram_stories: "Instagram",
  insta: "Instagram",
  fb: "Facebook",
  facebook: "Facebook",
  meta: "Meta Ads",
  threads: "Threads",
  tt: "TikTok",
  tiktok: "TikTok",
  yt: "YouTube",
  youtube: "YouTube",
  google: "Google",
  adwords: "Google Ads",
  googleads: "Google Ads",
  "google-ads": "Google Ads",
  bing: "Bing",
  pinterest: "Pinterest",
  twitter: "X (Twitter)",
  x: "X (Twitter)",
  linkedin: "LinkedIn",
  snapchat: "Snapchat",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  viber: "Viber",
  email: "Email",
  mail: "Email",
  newsletter: "Email",
  sms: "SMS",
  qr: "QR code",
  influencer: "Influencer",
};

// Ad-platform click ids - present even when the referrer is stripped
const CLICK_IDS = [
  ["fbclid", "Meta Ads"],
  ["ttclid", "TikTok"],
  ["gclid", "Google Ads"],
  ["gbraid", "Google Ads"],
  ["wbraid", "Google Ads"],
  ["msclkid", "Bing Ads"],
  ["twclid", "X (Twitter)"],
  ["epik", "Pinterest"],
  ["sccid", "Snapchat"],
  ["li_fat_id", "LinkedIn"],
];

// In-app browsers usually send no referrer, but they do announce themselves
const IN_APP_RULES = [
  [/Instagram/i, "Instagram"],
  [/FBAN|FBAV|FB_IAB|FBIOS/i, "Facebook"],
  [/BytedanceWebview|musical_ly|TikTok/i, "TikTok"],
  [/Pinterest/i, "Pinterest"],
  [/Snapchat/i, "Snapchat"],
  [/LinkedInApp/i, "LinkedIn"],
  [/Threads/i, "Threads"],
];

const clamp = (value, max) => String(value || "").trim().slice(0, max);

function titleCase(value) {
  return value
    .replace(/[_+-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
}

function matchReferrer(host) {
  const rule = REFERRER_RULES.find(([re]) => re.test(host));
  return rule ? rule[1] : "";
}

function matchInApp(ua) {
  const rule = IN_APP_RULES.find(([re]) => re.test(ua));
  return rule ? rule[1] : "";
}

function normalizeUtm(value) {
  const key = value.toLowerCase().trim();
  return UTM_ALIASES[key] || titleCase(value);
}

export function deviceType(ua = navigator.userAgent) {
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobi))/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return "mobile";
  return "desktop";
}

/**
 * Work out the source for the page currently being loaded. Pure, so it can be
 * unit-tested by passing a fake location/referrer/user-agent.
 */
export function detectSource(
  loc = typeof window !== "undefined" ? window.location : { search: "", hostname: "", pathname: "/" },
  ref = typeof document !== "undefined" ? document.referrer : "",
  ua = typeof navigator !== "undefined" ? navigator.userAgent : "",
) {
  const params = new URLSearchParams(loc.search || "");
  const get = (key) => (params.get(key) || "").trim();

  const utmSource = get("utm_source");
  const utmMedium = get("utm_medium");
  const campaign = get("utm_campaign");

  const clickRule = CLICK_IDS.find(([key]) => params.get(key));

  const refHost = hostOf(ref);
  const self = (loc.hostname || "").replace(/^www\./i, "");
  const external = Boolean(refHost) && refHost !== self && !refHost.endsWith(`.${self}`);

  // Most explicit signal first: the campaign tag, then the referring site,
  // then the in-app browser, then a bare ad click id. `how` records which one
  // answered, which is exactly what the medium should say when no utm_medium.
  let source = "";
  let how = "";
  if (utmSource) {
    source = normalizeUtm(utmSource);
    how = "campaign";
  }
  if (!source && external) {
    source = matchReferrer(refHost);
    if (source) how = "referral";
  }
  if (!source) {
    source = matchInApp(ua);
    if (source) how = "app";
  }
  if (!source && clickRule) {
    source = clickRule[1];
    how = "paid";
  }
  if (!source && external) {
    source = refHost;
    how = "referral";
  }
  if (!source) {
    source = DIRECT;
    how = "direct";
  }

  const medium = utmMedium || (clickRule ? "paid" : how);

  return {
    source: clamp(source, 64),
    medium: clamp(medium, 64),
    campaign: clamp(campaign, 128),
    referrer: clamp(external ? refHost : "", 255),
    // Path only - query strings carry click ids we have no reason to keep
    landingPage: clamp(loc.pathname || "/", 255),
  };
}

function read() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(value) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(value));
  } catch {
    /* private mode - attribution just won't persist */
  }
}

/**
 * Run on every route change. Cheap, and it lets a mid-visit click from a new
 * source (e.g. the customer comes back through an Instagram ad) take over.
 */
export function captureAttribution() {
  const detected = detectSource();
  const now = new Date();
  const stored = read();

  const expired =
    stored?.capturedAt &&
    now.getTime() - new Date(stored.capturedAt).getTime() > TTL_DAYS * 86400000;

  const takeOver =
    !stored ||
    expired ||
    (detected.source !== DIRECT && detected.source !== stored.source);

  const next = takeOver
    ? { ...detected, capturedAt: now.toISOString() }
    : { ...stored, ...(detected.campaign ? { campaign: detected.campaign } : {}) };

  next.lastSeenAt = now.toISOString();
  write(next);
  return next;
}

export function getAttribution() {
  return read() || captureAttribution();
}

/** Compact shape sent along with an order. */
export function attributionPayload() {
  const a = getAttribution();
  if (!a) return null;
  return {
    source: a.source,
    medium: a.medium || null,
    campaign: a.campaign || null,
    referrer: a.referrer || null,
    landingPage: a.landingPage || null,
  };
}

// Counted unless the visitor explicitly declined analytics cookies
function analyticsAllowed() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return true;
    return JSON.parse(raw)?.value !== "essential";
  } catch {
    return true;
  }
}

function randomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * One anonymous row per browser session - no cookies, no IP, no user agent
 * stored, just the source and a coarse device type. Safe to call on every
 * route change: it no-ops once the session has been counted.
 */
export function trackVisit() {
  if (!analyticsAllowed()) return;

  let sessionId;
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionId = randomId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    return;
  }

  const a = getAttribution();
  api
    .post("/api/track/visit", {
      sessionId,
      source: a.source,
      medium: a.medium,
      campaign: a.campaign,
      referrer: a.referrer,
      landingPage: a.landingPage,
      device: deviceType(),
    })
    .catch(() => {});
}
